import { resolveMount } from './resolve-mount';
import { signinUrl } from './signin-url';
import { clearChatsCache } from './chats-cache';
import { clearChatSession } from './chat-session';
import { clearInstance } from './instance-auth';
import { clearPendingProfileThread } from './profile-thread-target';

// Wix account logout. `users.wix.com/signout` is the canonical endpoint the Wix
// dashboard apps use — it clears the session and then sends the browser to
// `redirectTo`. We land on the Wix sign-in page rather than back on the app URL:
// returning to the (now unauthenticated) app immediately re-triggers the
// platform's OAuth re-auth, which both defeats the intent of "log out" and
// spirals into a redirect loop when the Wix cookies are in a stale state.
//
// The sign-in URL carries `postLogin` set to the current surface's home, so once
// the user signs back in they return to the right app — the Symphony root
// (symphony.wix.com/) or the /orion-web / /symphony-web mount they logged out from
// — instead of a bare sign-in page with nowhere to go.
export async function wixLogout(): Promise<void> {
  if (typeof window === 'undefined') return;
  clearChatSession();
  // The signed instance is identity-scoped state in `localStorage`, exactly like the chat session
  // and the chats cache beside it — and it was the one thing here that survived a sign-out. It
  // carries an `expirationDate`, so it does die on its own, but hours later: inside that window
  // `ensureInstance()` answers from the cache without a request, so someone who signs out and
  // straight back in as a DIFFERENT tenant gets handed the previous one's instance. (And a payload
  // with no parseable expiry is trusted indefinitely — see `fresh()`.) Drop it at the boundary,
  // which is the one moment we know for certain the account is changing.
  clearInstance();
  // A profile link tapped by THIS account must never open a thread for the next one to sign in here.
  clearPendingProfileThread();
  const mount = resolveMount(window.location.pathname);
  const home = `${window.location.origin}${mount || '/'}`;
  const signIn = signinUrl(home);
  const params = new URLSearchParams({
    originUrl: window.location.href,
    redirectTo: signIn,
  });
  const destination = `https://users.wix.com/signout?${params.toString()}`;
  // Give the optional disk cache a bounded chance to finish deletion before the page is unloaded.
  // Identity checks also prevent another account from reading a retained page if storage is blocked.
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([clearChatsCache(), new Promise<void>((resolve) => { timeout = setTimeout(resolve, 300); })]);
  } finally {
    clearTimeout(timeout);
    window.location.href = destination;
  }
}
